'use client';

import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Edit } from "lucide-react";
import { useGetAllUsersQuery } from "@/features/api/authApi";
import { useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";

const UserList = () => {
  const { data, isLoading, error } = useGetAllUsersQuery();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");

  /* ---------------- NORMALIZE USERS ---------------- */
  const users = useMemo(() => {
    return (data?.users || []).map((u) => ({
      ...u,
      name: u.name || "N/A",
      email: u.email || "N/A",
      role: u.role || "user",
      phone: u.phone || u.mobile || u.tel || "N/A",
      createdAt: u.createdAt || null,
    }));
  }, [data]);

  /* ---------------- FILTER ---------------- */
  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;

    const q = searchQuery.toLowerCase();

    return users.filter((u) =>
      String(u.name).toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  /* ---------------- SORT ---------------- */
  const sortedUsers = useMemo(() => {
    const list = [...filteredUsers];

    return list.sort((a, b) => {
      const dir = sortOrder === "asc" ? 1 : -1;

      if (sortBy === "date") {
        const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return (aDate - bDate) * dir;
      }

      if (sortBy === "name") {
        return a.name.localeCompare(b.name) * dir;
      }

      if (sortBy === "role") {
        return a.role.localeCompare(b.role) * dir;
      }

      return 0;
    });
  }, [filteredUsers, sortBy, sortOrder]);

  const toggleSortOrder = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  if (isLoading) return <p className="p-4">Loading users...</p>;
  if (error) return <p className="p-4 text-red-500">Failed to load users.</p>;

  return (
      <div className="flex-1 p-6 bg-background min-h-screen">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <h2 className="text-2xl font-semibold">👥 All Users</h2>
          <Button>+ Create User</Button>
        </div>


      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name"
          className="p-2 border rounded w-full sm:max-w-xs"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <Table>
        <TableCaption>A list of all registered users</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>#</TableHead>
            <TableHead onClick={() => toggleSortOrder("name")} className="cursor-pointer">
              Name {sortBy === "name" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
            </TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead onClick={() => toggleSortOrder("role")} className="cursor-pointer">
              Role {sortBy === "role" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
            </TableHead>
            <TableHead onClick={() => toggleSortOrder("date")} className="cursor-pointer">
              Date {sortBy === "date" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
            </TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {sortedUsers.map((user, index) => {
            const formattedDate = user.createdAt
              ? new Date(user.createdAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              : "N/A";

            return (
              <TableRow key={user._id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.phone}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>{formattedDate}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigate(`/admin/edit-user/${user._id}`)}
                    aria-label={`Edit user ${user.name}`}
                  >
                    <Edit size={18} />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default UserList;
